import { useEffect, useState } from "react";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/Key";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Popper from "@mui/material/Popper";

import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as lodash from "lodash";

import { FileUploadPreview } from "./FileUploadPreview";
import { PasswordInput } from "./Password";
import { http_post, uploadToS3 } from "./Auth";
import * as u from "./utility";
import log from "./logger";
import { encrypt, decrypt } from "./crypto";

export const MAX_MESSAGE_SIZE = 4000;

function UploadErrorToast({ onRetry }) {
  return (
    <Stack direction={"row"} alignItems={"center"}>
      <Typography fontSize={12}>File upload error</Typography>
      <Button onClick={onRetry} size="small">
        RETRY
      </Button>
    </Stack>
  );
}

export function handlePasteEvent(e, processFiles, setRefs) {
  let clipboardItems = e.clipboardData.items;

  let files = [];

  // first handle files and text
  for (let i = 0; i < clipboardItems.length; ++i) {
    const item = clipboardItems[i];
    if (item.kind === "file") {
      const file = item.getAsFile();
      files.push(file);
    } else if (item.kind === "string") {
      if (item.type == "text/plain") {
        const txt = e.clipboardData.getData("text");
        if (txt.length > MAX_MESSAGE_SIZE) {
          const largeTextFile = new File([txt], "description.txt", {
            type: "text/plain",
          });
          files.push(largeTextFile);
        }
      }
    }
  }

  // lastly handle html. the processFiles has to be called once
  for (let i = 0; i < clipboardItems.length; ++i) {
    const item = clipboardItems[i];
    if (item.kind === "string") {
      if (item.type === "text/html") {
        const downloadHtmlImages = async (images, files) => {
          for (let i = 0; i < images.length; ++i) {
            const img = images.item(i);
            const getTopstorieName = (img) => {
              const attr = img.attributes.getNamedItem("st_filename");
              return attr ? attr.nodeValue : undefined;
            };

            try {
              const response = await fetch(img.src);
              const data = await response.blob();
              if (data.type.startsWith("image/")) {
                let nameFromUrl =
                  getTopstorieName(img) ||
                  (!img.src.startsWith("data:") && img.src.split("/").pop()) ||
                  uuidv4();

                const filetype = data.type.split("/").pop();
                if (!nameFromUrl.endsWith("." + filetype)) {
                  nameFromUrl += "." + filetype;
                }

                const imageFile = new File([data], nameFromUrl, {
                  type: data.type,
                });
                files.push(imageFile);
              }
            } catch (e) {
              log.debug(e);
            }
          }

          if (files.length > 0) {
            processFiles(files);
          }
        };

        const parser = new DOMParser();
        const html = e.clipboardData.getData("text/html");
        const doc = parser.parseFromString(html, "text/html");
        if (doc.images.length > 0) {
          downloadHtmlImages(doc.images, files);
          files = [];
        }

        let refs = [];
        extractRefsFromHtml(doc, refs);

        if (refs.length > 0) {
          setRefs((prev) => {
            let res = [];
            let seen = new Set();

            const proc = (refs) => {
              for (let r of refs) {
                const key = r.PK + ":" + r.SK;
                if (!seen.has(key)) {
                  seen.add(key);
                  res.push(r);
                }
              }
            };

            proc(prev);
            proc(refs);
            return prev.length == res.length ? prev : res;
          });
        }
      }
    }
  }

  if (files.length > 0) {
    processFiles(files);
  }

  if (files.length == clipboardItems.length) {
    e.preventDefault();
  }
}

function TextToast({ text }) {
  return <Typography fontSize={12}>{text}</Typography>;
}

export function validateNotifyFileSize(file) {
  if (file.size > 50 * 1024 * 1024) {
    let abbrev = file.name;
    if (abbrev.length > 16) {
      abbrev = abbrev.slice(0, 13) + "...";
    }

    const text = `Sorry, the file "${abbrev}" is too big.`;
    toast.error(<TextToast text={text} />);
    return false;
  }

  return true;
}

function extractRefsFromHtml(doc, refs) {
  for (const child of doc.children) {
    const pk = child.attributes.getNamedItem("st_pk");
    if (pk) {
      const sk = child.attributes.getNamedItem("st_sk");
      const ref = { PK: pk.nodeValue, SK: sk.nodeValue };

      const found = refs.find((e) => lodash.isEqual(ref, e));
      if (!found) {
        refs.push(ref);
      }
    }
    extractRefsFromHtml(child, refs);
  }
}

const unfinishedMessage = (item) => {
  return u.readLocalStorage(`SendMessageBox:${item.PK}:${item.SK}`, "");
};

export function SendMessageBox({
  item,
  setMessages,
  id,
  onSent,
  droppedFiles,
  onFileRemoved,
}) {
  const [disabled, setDisabled] = useState(!unfinishedMessage(item));
  const [message, setMessage] = useState(unfinishedMessage(item));
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [refs, setRefs] = useState([]);
  const [previousMessage, setPreviousMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [password, setPassword] = useState("");

  const processTextMessage = (value) => {
    setDisabled(value.length === 0 && selectedFiles.length === 0);
    const truncated = value.slice(0, MAX_MESSAGE_SIZE);
    setMessage(truncated);
  };

  const handleChange = (e) => {
    processTextMessage(e.target.value);
  };

  const handleSend = () => {
    if (
      (!message || message.length == 0) &&
      (!selectedFiles || selectedFiles.length == 0)
    ) {
      return;
    }

    const uploadFiles = async (ts, encrypted_message) => {
      const uploaded = [];
      for (let f of selectedFiles) {
        const uri = await uploadToS3(f);
        if (uri) {
          uploaded.push({ uri: uri, type: f.type, size: f.size, name: f.name });
        } else {
          return false;
        }
      }

      const uploadRequest = async (message, files) => {
        try {
          const updateUiPreferences = (message) => {
            setTimeout(() => {
              http_post("stories", { messages: true, story: item.SK })
                .then((response) => {
                  const latest = response.data.messages.find(
                    (e) => e.SK === message.SK
                  );
                  if (latest && latest.st_ui) {
                    setMessages((prev) => {
                      const found = prev.findIndex((e) => e.SK === message.SK);
                      if (found >= 0) {
                        let res = [...prev];
                        res[found] = { ...res[found], st_ui: latest.st_ui };
                        return res;
                      } else {
                        return prev;
                      }
                    });
                  }
                })
                .catch((e) => {
                  log.debug(e);
                });
            }, 3000);
          };

          let obj = { reply: true, story: item.SK, refs: refs };
          if (files && files.length > 0) {
            obj.files = files;
          }

          if (encrypted_message) {
            obj.enc = encrypted_message;
          } else {
            if (message && message.length > 0) {
              obj.desc = message;
            }
          }

          if (item.st_source) {
            obj.forward = item.st_source;
          }

          http_post("stories", obj).then((response) => {
            u.writeLocalStorage(`SendMessageBox:${item.PK}:${item.SK}`, "");
            if (onSent) {
              onSent({ ts: ts, msg: response.data.reply });
            }
            if (files && files.length > 0) {
              updateUiPreferences(response.data.reply);
            }
          });
        } catch (err) {
          log.debug(err);
        }
      };

      uploadRequest(message, uploaded);
      return true;
    }; // uploadFiles

    const ts = u.getCurrentTimestamp();
    let messageObj = {
      SK: ts,
      ts: ts,
      event_ts: ts,
      type: "message",
      refs: refs,
      dummy: true,
    };

    if (message && !password) {
      messageObj.text = message;
    }

    if (selectedFiles.length > 0) {
      messageObj.files = selectedFiles;
      messageObj.type = "topstorie_file_upload";
    }

    const proceed = async () => {
      let encrypted_message = undefined;
      if (password && message) {
        encrypted_message = await encrypt(password, message);
      }

      const attemptUpload = async () => {
        const ok = await uploadFiles(ts, encrypted_message);
        if (ok) {
          if (onSent) {
            // update the thread, it will remove the dummy messages and
            // render pictures downloaded from cache, timeout is needed in order to give
            // the cache a bit of time to populate, because the cache is async
            setTimeout(() => onSent(null), 2000);
          }

          return;
        }

        setMessages((prev) => {
          const found = prev.find((m) => m.SK === ts);
          if (found) {
            found.error = "upload error";
            return [...prev];
          }
          return prev;
        });

        toast.error(<UploadErrorToast item={item} onRetry={attemptUpload} />);
      };

      setMessages((prev) => (prev ? [...prev, messageObj] : [messageObj]));

      attemptUpload();
      setPreviousMessage(message);
      setMessage("");
      setDisabled(true);
      setSelectedFiles([]);
      setPassword("");
    }; // proceed

    proceed();
  }; // handleSend

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key == "ArrowUp") {
      if (!message) {
        setMessage((prev) => {
          if (!prev) {
            return previousMessage;
          } else {
            return prev;
          }
        });
        e.preventDefault();
      }
    }
  }; // handleKey

  const processFiles = (files) => {
    if (!files.length) {
      return;
    }

    const isDuplicate = (file) => {
      for (let f of selectedFiles) {
        if (
          file.name === f.name &&
          file.size === f.size &&
          file.type === f.type
        ) {
          return true;
        }
      }
      return false;
    };

    let newFiles = [];
    for (let fileToUpload of files) {
      if (!isDuplicate(fileToUpload)) {
        if (validateNotifyFileSize(fileToUpload)) {
          newFiles.push(fileToUpload);
        }
      }
    }
    if (newFiles.length) {
      setSelectedFiles([...selectedFiles, ...newFiles]);
      setDisabled(false);
    }
  }; // processFiles

  const handleFileSelected = (e) => {
    processFiles(e.target.files);
  };

  const handleDelete = (index) => {
    let trimmed = [...selectedFiles];
    trimmed.splice(index, 1);
    setSelectedFiles(trimmed);
    if (onFileRemoved) {
      onFileRemoved();
    }
  };

  const handleBlur = () => {
    u.writeLocalStorage(`SendMessageBox:${item.PK}:${item.SK}`, message);
  };

  const handlePaste = (e) => {
    handlePasteEvent(e, processFiles, setRefs, (text) => {
      setMessage((prev) => (prev + text).slice(0, MAX_MESSAGE_SIZE));
      setDisabled(
        message.length === 0 && text.length === 0 && selectedFiles.length === 0
      );
    });
  };

  const handleRequestPassword = (e) => {
    setAnchorEl((prev) => (prev ? null : e.currentTarget));
  };

  const handlePasswordCancel = (e) => {
    setAnchorEl(null);
  };

  const handleSetPassword = (password) => {
    setPassword(password);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (droppedFiles && !password) {
      processFiles(droppedFiles);
    }
  }, [droppedFiles]);

  return (
    <Stack width="100%">
      <TextField
        label="Want to add anything to the story?"
        width="100%"
        size="small"
        value={message}
        fullWidth
        multiline
        onChange={handleChange}
        onKeyDown={handleKey}
        onBlur={handleBlur}
        onPaste={handlePaste}
        variant="standard"
        id={anchorEl ? "color-picker-popper" : undefined}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {true && (
                <>
                  <input
                    type="file"
                    id={`file-upload-${item.SK}-${id}`}
                    hidden
                    onChange={handleFileSelected}
                    multiple
                    disabled={!!password}
                  />
                  <label htmlFor={`file-upload-${item.SK}-${id}`}>
                    <IconButton component="span" disabled={!!password}>
                      <AttachFileIcon />
                    </IconButton>
                  </label>
                </>
              )}
              <IconButton
                // edge="end"
                color={password ? undefined : "primary"}
                disabled={disabled || selectedFiles.length > 0}
                onClick={handleRequestPassword}
                sx={{ color: password ? "red" : undefined }}
              >
                <KeyIcon />
              </IconButton>
              <IconButton
                edge="end"
                color="primary"
                disabled={disabled || (!!password && selectedFiles.length > 0)}
                onClick={handleSend}
              >
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            alignItems: "flex-start",
          },
        }}
      />
      <Popper
        id={anchorEl ? "color-picker-popper" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
      >
        <PasswordInput
          password={password}
          onPassword={handleSetPassword}
          onCancel={handlePasswordCancel}
          label={"Protect message with password"}
        />
      </Popper>
      <FileUploadPreview
        item={item}
        files={selectedFiles}
        onDelete={handleDelete}
      />
    </Stack>
  );
}
